/**
 * Reset everyone's password in one client's workspace (FEAT-690).
 *
 * The portal does not reset anybody itself. The client access Lambda
 * (`passwordReset.preview`, `passwordReset.status`, `passwordReset.start`)
 * invokes the client's own admin-password-reset Lambda with
 * `{"portal": "preview" | "status" | "start"}`, so a reset from here runs the
 * same code, with the same checks, as a reset an admin starts on the client's
 * Users page (lambdas/node/admin-password-reset). The client's Lambda records
 * the person signed in as who started it.
 *
 * Two calls, never one: a preview lists who it reaches and returns a token
 * for that list, and a start needs the token and the client name, typed.
 */
import type { Client } from '@/types';
import { activityService } from './activityService';
import { clientAccess } from './clientAccess';

export type ResetSkipReason =
  | 'you'
  | 'system-user'
  | 'single-sign-on'
  | 'disabled'
  | 'already-reset'
  | 'not-activated'
  | 'no-verified-email';

export interface ResetRun {
  runId: string;
  status: 'running' | 'finished' | 'stopped';
  source: 'admin' | 'portal';
  by: string;
  startedAt: number;
  finishedAt: number | null;
  total: number;
  done: number;
  failedCount: number;
  failures: { email: string; error: string }[];
}

export interface ResetPreview {
  workspace: string;
  reset: string[];
  skipped: { email: string; reason: ResetSkipReason }[];
  waiting: string[];
  previewToken: string;
  expiresAt: number;
  run: ResetRun | null;
}

/** A start the Lambda refused. `preview` is the new list when the old one is out of date. */
export class ResetRefused extends Error {
  constructor(
    public reason: string,
    message: string,
    public preview?: ResetPreview,
    public run?: ResetRun | null
  ) {
    super(message);
  }
}

const REFUSAL_TEXT: Record<string, string> = {
  confirm: 'The name you typed is not the client name.',
  by: 'The portal could not say who you are. Sign in again.',
  'preview-missing': 'Check the list again, then confirm.',
  'preview-expired': 'The list is more than 15 minutes old. Check it again, then confirm.',
  'preview-changed': 'The list of people changed. Check it again, then confirm.',
  running: 'A reset is already running in this workspace.',
  nobody: 'Nobody in this workspace can be reset.',
};

/** What `passwordReset.start` answers: the run it started, or why the client's Lambda refused. */
type StartAnswer = { run: ResetRun } | { refused: string; preview?: ResetPreview; run?: ResetRun | null };

export const passwordResetService = {
  /** Who the reset reaches, who it leaves out, and the last run. */
  async preview(client: Client): Promise<ResetPreview> {
    const { preview } = await clientAccess<{ preview: ResetPreview }>('passwordReset.preview', {
      clientName: client.name,
    });
    return preview;
  },

  /** The same answer as `preview`, for the page's poll while a run is going. */
  async status(client: Client): Promise<ResetPreview> {
    const { preview } = await clientAccess<{ preview: ResetPreview }>('passwordReset.status', {
      clientName: client.name,
    });
    return preview;
  },

  /** `by` goes to the portal's activity log only: the client's Lambda records the person signed in. */
  async start(client: Client, by: string, previewToken: string, confirm: string): Promise<ResetRun> {
    const answer = await clientAccess<StartAnswer>('passwordReset.start', {
      clientName: client.name,
      previewToken,
      confirm,
    });
    if ('refused' in answer) {
      throw new ResetRefused(
        answer.refused,
        REFUSAL_TEXT[answer.refused] ?? answer.refused,
        answer.preview,
        answer.run
      );
    }
    const { run } = answer;
    await activityService.logActivity({
      type: 'user',
      action: 'updated',
      resourceType: 'password-reset',
      resourceId: client.name,
      details: { metadata: { action: 'reset-everyone', by, total: run.total, runId: run.runId } },
      success: true,
    });
    return run;
  },
};
